import React, {useReducer, useRef} from 'react'
import moment from 'moment'

import EditActivity from '../../../../components/settings/activities/EditActivity.js'
import {Button, Card, Colors, H3, H5, Icon, IconButton, ListItem, P, Popup, Row, SortableList, Panel} from '../../../../components/UI/index.js'
import {Editor, FormRow, FormStack, Input, MetricInput, Select, Toggle} from '../../../../components/UI/forms/index.js'
import {Activity, Reseller} from '../../../../../server/functions/resellers/resellers.types.js'
import guid from '../../../../../utils/guid.js'
import Languages from '../../../../../utils/Languages.js'
import {User} from '../../../../../server/functions/users/users.types.js'
import shiftTooLateStartTemplate from '../../../../../server/functions/mail/templates/shiftTooLateStartTemplate.js'
import {showShiftLateStartNotification} from 'src/socketio/socketio.js'
import t from 'src/helpers/Languages.js'
import type {PopupRef} from '../../../../components/UI/overlays/Popup.js'

interface ActivitySettingsProps {
    reseller: Reseller
    onChange: (reseller: Reseller, path?: string) => void
    users: User[]
}

interface ActivitiesState {
    selectedActivity: Activity | null
    showEmailSettings: boolean
}

export default function Activities({reseller, onChange, users}: ActivitySettingsProps) {
    const [state, setState] = useReducer((state: ActivitiesState, newState: Partial<ActivitiesState>) => ({...state, ...newState}), {
        selectedActivity: null,
        showEmailSettings: false
    })

    const popupRef = useRef<PopupRef>(null)

    const addActivity = () => {
        const newActivity: Activity = {
            id: guid.generate(),
            name: '',
            color: Colors.routeColors[Math.floor(Math.random() * Colors.routeColors.length)],
            trigger: '',
            automaticOnly: false,
            lockAppActivity: false
        }

        reseller.settings.shifts.activities.push(newActivity)
        onChange(reseller, 'shifts.activities')

        setState({selectedActivity: newActivity})
    }

    const removeActivity = (event: React.MouseEvent, activity: Activity) => {
        event.stopPropagation()

        popupRef.current?.open('Activiteit verwijderen', 'Weet je zeker dat je deze activiteit wilt verwijderen?', () => {
            const index = reseller.settings.shifts.activities?.findIndex((ruleSet) => ruleSet.id === activity.id)

            if (index !== -1) {
                reseller.settings.shifts.activities.splice(index, 1)
                onChange(reseller, 'shifts.activities')
            }

            if (state.selectedActivity?.id === activity.id) {
                setState({selectedActivity: null})
            }

            popupRef.current?.close()
        })
    }

    return (
        <Row style={{gap: 36, justifyContent: 'flex-start'}}>
            <Card shadow style={{display: 'flex', flexDirection: 'column', padding: 20, width: 600, height: 'fit-content'}}>
                <H3 style={{marginBottom: 36}}>{Languages.shifts['NL']}</H3>

                <P>{Languages.shiftSettingsInfo['NL']}</P>

                <H5 style={{marginBottom: 24, marginTop: 36}}>{Languages.activities['NL']}</H5>

                {reseller.settings.shifts.activities.length > 0 &&
                    <Panel style={{flex: 1, overflowY: 'auto', maxHeight: 500, marginBottom: 24}}>
                        <SortableList
                            items={reseller.settings.shifts.activities || []}
                            onChange={(activities: Activity[]) => {
                                reseller.settings.shifts.activities = activities
                                onChange(reseller, 'shifts.activities')
                            }}
                            renderItem={(activity: Activity) => {
                                return (
                                    <ListItem
                                        key={activity.id}
                                        icon='mdi mdi-drag-horizontal'
                                        active={state.selectedActivity?.id === activity.id}
                                        onClick={() => {
                                            setState({selectedActivity: activity})
                                        }}
                                    >
                                        <Icon
                                            style={{marginRight: 6}}
                                            icon='mdi mdi-circle'
                                            color={activity.color}
                                        />
                                        {activity.name}

                                        <div style={{flex: 1}} />

                                        <IconButton
                                            style={{marginLeft: 6}}
                                            icon='mdi mdi-delete'
                                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => removeActivity(event, activity)}
                                        />
                                    </ListItem>
                                )
                            }}
                        />
                    </Panel>
                }

                <Button
                    variant='outline'
                    icon='mdi mdi-plus'
                    label={Languages.addActivity['NL']}
                    onClick={addActivity}
                />

                <H5 style={{marginBottom: 24, marginTop: 36}}>{Languages.settings['NL']}</H5>

                <FormStack>
                    <Toggle
                        label='Dienst starten verplicht'
                        info='Werkzaamheden kunnen alleen worden uitgevoerd als een dienst is gestart.'
                        checked={reseller.settings.shifts.shiftRequired}
                        onChange={(event) => {
                            reseller.settings.shifts.shiftRequired = event.target.checked
                            onChange(reseller, 'shifts.shiftRequired')
                        }}
                    />

                    <Toggle
                        label='GPS verplicht'
                        info=''
                        checked={reseller.settings.shifts.gpsRequired}
                        onChange={(event) => {
                            reseller.settings.shifts.gpsRequired = event.target.checked
                            onChange(reseller, 'shifts.gpsRequired')
                        }}
                    />

                    <Select
                        label='Afwijken van startlocatie'
                        info=''
                        value={reseller.settings.shifts.deviateStartLocation}
                        onChange={(event) => {
                            reseller.settings.shifts.deviateStartLocation = event.target.value as '' | 'warning' | 'notallowed'
                            onChange(reseller, 'shifts.deviateStartLocation')
                        }}
                    >
                        <option value=''>Toestaan</option>
                        <option value='warning'>Toestaan met waarschuwing</option>
                        <option value='notallowed'>Niet toestaan</option>
                    </Select>

                    <Select
                        label='Afwijken van eindlocatie'
                        info=''
                        value={reseller.settings.shifts.deviateEndLocation}
                        onChange={(event) => {
                            reseller.settings.shifts.deviateEndLocation = event.target.value as '' | 'warning' | 'notallowed'
                            onChange(reseller, 'shifts.deviateEndLocation')
                        }}
                    >
                        <option value=''>Toestaan</option>
                        <option value='warning'>Toestaan met waarschuwing</option>
                        <option value='notallowed'>Niet toestaan</option>
                    </Select>

                    {(reseller.settings.shifts.deviateStartLocation || reseller.settings.shifts.deviateEndLocation) &&
                        <MetricInput
                            label='Maximaal afwijken van start-/eindlocatie'
                            value={reseller.settings.shifts.maxStartEndLocationDeviation}
                            onChange={(event) => {
                                console.log(event.target.value)
                                reseller.settings.shifts.maxStartEndLocationDeviation = event.target.value
                                onChange(reseller, 'shifts.maxStartEndLocationDeviation')
                            }}
                        />

                    }

                    <Select
                        label={Languages.startShiftBeforeStartTime['NL']}
                        info='Bepaalt of een koerier een dienst kan starten voordat de geplande starttijd is bereikt.'
                        value={reseller.settings.shifts.startShiftBeforeStartTime}
                        onChange={(event) => {
                            reseller.settings.shifts.startShiftBeforeStartTime = event.target.value as 'allowed' | 'allowedmargin' | 'notallowed'
                            onChange(reseller, 'shifts.startShiftBeforeStartTime')
                        }}
                    >
                        <option value='allowed'>{Languages.allowed['NL']}</option>
                        <option value='allowedmargin'>{Languages.allowedWithMargin['NL']}</option>
                        <option value='notallowed'>{Languages.notAllowed['NL']}</option>
                    </Select>

                    {reseller.settings.shifts.startShiftBeforeStartTime === 'allowedmargin' &&
                        <MetricInput
                            label={t('startShiftBeforeStartTimeMargin')}
                            info='Aantal minuten voor de geplande starttijd dat de dienst gestart mag worden.'
                            append='min'
                            min={0}
                            value={reseller.settings.shifts.startShiftBeforeStartTimeMargin}
                            onChange={(event) => {
                                reseller.settings.shifts.startShiftBeforeStartTimeMargin = event.target.value
                                onChange(reseller, 'shifts.startShiftBeforeStartTimeMargin')
                            }}
                        />
                    }

                    <Select
                        label='Waarschuwing bij te laat starten dienst'
                        value={reseller.settings.shifts.lateStart.warning}
                        onChange={(event) => {
                            reseller.settings.shifts.lateStart.warning = event.target.value as Reseller['settings']['shifts']['lateStart']['warning']
                            onChange(reseller, 'shifts.lateStart.warning')
                        }}
                    >
                        <option value=''>Geen</option>
                        <option value='os'>OS notificatie</option>
                        <option value='email'>Email</option>
                        <option value='both'>OS notificatie + email</option>
                    </Select>

                    {reseller.settings.shifts.lateStart.warning !== '' &&
                        <FormRow>
                            {['os', 'both'].includes(reseller.settings.shifts.lateStart.warning) && (
                                <Button
                                    style={{flex: 1}}
                                    onClick={() => {
                                        showShiftLateStartNotification({shiftId: '', shiftName: 'ABC'})
                                    }}
                                >
                                    Test OS notificatie
                                </Button>
                            )}

                            {['email', 'both'].includes(reseller.settings.shifts.lateStart.warning) && (
                                <Button
                                    style={{flex: 1}}
                                    onClick={() => {
                                        setState({showEmailSettings: true, selectedActivity: null})
                                    }}
                                >
                                    Email instellen
                                </Button>
                            )}
                        </FormRow>
                    }
                </FormStack>
            </Card>

            {state.selectedActivity &&
                <EditActivity
                    activity={state.selectedActivity}
                    onSave={(activity) => {
                        const index = reseller.settings.shifts.activities?.findIndex((ruleSet) => ruleSet.id === activity.id)
                        reseller.settings.shifts.activities[index] = activity
                        onChange(reseller, 'shifts.activities')
                    }}
                    onCancel={() => setState({selectedActivity: null})}
                />
            }

            {state.showEmailSettings &&
                <div>
                    <Card shadow style={{padding: 20, width: 626}}>
                        <H3 style={{marginBottom: 36}}>Email instellingen</H3>

                        <FormStack>
                            <Editor
                                label='Onderwerp'
                                as='input'
                                tags={['klant', 'naam', 'referentie', 'vestiging', 'koerier', 'datum', 'starttijd']}
                                value={reseller.settings.shifts.lateStart?.subject}
                                onChange={(e) => {
                                    reseller.settings.shifts.lateStart.subject = e.target.value
                                    onChange(reseller, 'shifts.lateStart.subject')
                                }}
                                info='Typ "{" om tags te gebruiken in het onderwerp.'
                            />

                            <Input
                                label='Afzender email'
                                value={reseller.settings.shifts.lateStart?.senderEmail}
                                onChange={(e) => {
                                    reseller.settings.shifts.lateStart.senderEmail = e.target.value
                                    onChange(reseller)
                                }}
                                onBlur={(e) => {
                                    reseller.settings.shifts.lateStart.senderEmail = e.target.value
                                    onChange(reseller, 'shifts.lateStart.senderEmail')
                                }}
                            />

                            <Input
                                label='Verstuur na hoeveel minuten na verstrijken starttijd'
                                value={typeof reseller.settings.shifts.lateStart.notificationMinutes === 'number' ? reseller.settings.shifts.lateStart.notificationMinutes : ''}
                                type='number'
                                onChange={(e) => {
                                    const value = parseInt(e.target.value)
                                    if (!isNaN(value)) {
                                        reseller.settings.shifts.lateStart.notificationMinutes = value
                                        onChange(reseller)
                                    } else {
                                        reseller.settings.shifts.lateStart.notificationMinutes = null
                                        onChange(reseller)
                                    }
                                }}
                                onBlur={(e) => {
                                    const value = parseInt(e.target.value)
                                    if (!isNaN(value)) {
                                        reseller.settings.shifts.lateStart.notificationMinutes = value
                                        onChange(reseller, 'shifts.lateStart.notificationMinutes')
                                    } else {
                                        reseller.settings.shifts.lateStart.notificationMinutes = null
                                        onChange(reseller, 'shifts.lateStart.notificationMinutes')
                                    }
                                }}
                                allowEmptyNumber
                            />

                            <Select
                                label='Ontvanger(s) alle meldingen'
                                info='Kies hier welke gebruikers een melding krijgen voor alle te laat gestarte diensten. Je kan dit ook per dienst (template) instellen.'
                                values={reseller.settings.shifts.lateStart.recipients}
                                options={users.map((user) => ({value: user._id, title: user.name}))}
                                allowCustomValues
                                onChange={(e) => {
                                    reseller.settings.shifts.lateStart.recipients = e.target.values
                                    onChange(reseller, 'shifts.lateStart.recipients')
                                }}
                            />
                        </FormStack>
                    </Card>

                    <Card shadow style={{marginTop: 36, padding: 0, width: 626}}>
                        <div dangerouslySetInnerHTML={{__html: shiftTooLateStartTemplate({
                            logo: reseller.settings.logo || '/images/logoPlaceholder.png',
                            color: reseller.settings.color || Colors.brandColor,
                            shift: {
                                name: 'ABC',
                                date: moment().format('YYYY-MM-DD'),
                                plannedStartTime: '12:00',
                                startPoint: {
                                    street: 'Straatnaam',
                                    nr: '1',
                                    postalCode: '1111AA',
                                    city: 'Plaats'
                                },
                                plannedEndTime: '17:00',
                                endPoint: {
                                    street: 'Straatnaam',
                                    nr: '1',
                                    postalCode: '1111AA',
                                    city: 'Plaats'
                                },
                                reference: '-',
                                instructions: '-'
                            },
                            messengerName: 'Koeriernaam',
                            customerName: 'Klantnaam',
                            branchName: reseller.settings.branches?.[0]?.name || 'Vestigingsnaam',
                            reseller,
                            noButtonClick: true
                        }, window.location.origin)
                        }}
                        />
                    </Card>
                </div>
            }

            <Popup ref={popupRef} />
        </Row>
    )
}
